import {CreateLeadRequest, Lead, LeadInteraction} from "shared-types";
import {UUIDTypes} from "uuid";
import {create} from "zustand";
import {leadApi} from '../../services/api';

interface LeadsState {
    leads: Lead[];
    isEditModalOpen: boolean,
    editingInteraction: LeadInteraction | null,
    selectedLead: Lead | null;
    loading: boolean;
    error?: string;
    showGraphForId: string | null;
    fetchLeads: () => Promise<void>;
    handleSelectLead: (leadId: string | null) => void;
    handleDeleteLead: (leadId: string) => Promise<void>;
    handleCreateLead: (lead: CreateLeadRequest) => Promise<Lead | undefined>;
    handleCreateInteraction: (lead: Lead) => Promise<Response>;
    handleUpdateLead: (leadId: string, lead: Partial<Lead>) => Promise<Lead>;
    resetSelectedLead: () => void;
    updateSelectedLead: (lead: Lead) => void;
    fetchLeadDetails: (leadId: string) => Promise<Lead>;
    setShowGraphForId: (id: string | null) => void;
    setIsEditModalOpen: (flag: boolean) => void;
    setEditingInteraction: (interaction: LeadInteraction | null) => void;
    handleDeleteInteraction: (interactionId: string) => Promise<void>;
    // --- תוספות להעלאת קובץ אקסל ---
    uploadFile: File | null;
    uploadStatus: "idle" | "uploading" | "success" | "error";
    uploadMessage: string;
    setUploadFile: (file: File | null) => void;
    uploadExcelFile: () => Promise<void>;
}

export const useLeadsStore = create<LeadsState>((set, get) => ({
        leads: [],
        selectedLead: null,
        loading: false,
        error: undefined,
        showGraphForId: null,
        isEditModalOpen: false,
        editingInteraction: null,
        // --- סטייטים לתוספות העלאת קובץ אקסל ---
        uploadFile: null,
        uploadStatus: "idle",
        uploadMessage: "",
        fetchLeads: async () => {
            set({loading: true, error: undefined});
            try {
                const data = await leadApi.getAll();
                console.log("🔍 fetchLeads - received data:", data);
                console.log("🔍 fetchLeads - first lead interactions:", data[0]?.interactions);
                set({leads: data, loading: false});
            } catch (error: any) {
                set({error: error.message || "שגיאה בטעינת הלידים", loading: false});
            }
        },
        handleSelectLead: (leadId: UUIDTypes | null) => {
            if (leadId === null) {
                set({selectedLead: null, isEditModalOpen: false, editingInteraction: null});
                return;
            }
            set((state) => ({
                selectedLead: state.leads.find(lead => lead.id === leadId),
                isEditModalOpen: false,
                editingInteraction: null
            }));
        },
        handleDeleteLead: async (leadId: UUIDTypes) => {
            set({loading: true, error: undefined});
            try {
                await leadApi.delete(leadId as string);
                await useLeadsStore.getState().fetchLeads();
            } catch (error: any) {
                set({error: error.message || "שגיאה במחיקת מתעניין", loading: false});
            } finally {
                set({loading: false});
            }
        },
        handleCreateLead: async (lead: CreateLeadRequest) => {
            set({loading: true, error: undefined});
            try {
                const newLead = await leadApi.create(lead);
                await useLeadsStore.getState().fetchLeads();
                return newLead;
            } catch (error: any) {
                set({error: error.message || "שגיאה ביצירת מתעניין", loading: false});
                console.log(error)
                return undefined;
            } finally {
                set({loading: false});
            }
        },
        handleUpdateLead: async (leadId: UUIDTypes, lead: Partial<Lead>): Promise<Lead> => {
            set({loading: true, error: undefined});
            try {
                const updatedLead = await leadApi.update(leadId as string, lead);
                await useLeadsStore.getState().fetchLeads();
                return updatedLead;
            } catch (error: any) {
                set({error: error.message || "שגיאה בעדכון מתעניין", loading: false});
                throw error; // חשוב לזרוק את השגיאה כדי שההבטחה תיכשל ולא תחזור undefined
            } finally {
                set({loading: false});
            }
        },
        resetSelectedLead: () => {
            set({selectedLead: null, isEditModalOpen: false, editingInteraction: null});
        },
        updateSelectedLead: (lead: Lead) => {
            set({selectedLead: lead});
        },
        fetchLeadDetails: async (leadId: string) => {
            set({loading: true, error: undefined});
            try {
                const data = await leadApi.getById(leadId);
                set({selectedLead: data, loading: false});
                return data;
            } catch (error: any) {
                set({error: error.message || "שגיאה בטעינת פרטי הליד", loading: false});
                return {} as Lead;
            }
        },
        setShowGraphForId: (id: string | null) => {
            set({showGraphForId: id});
        },

        setIsEditModalOpen(flag: boolean) {
            set({isEditModalOpen: flag})
        },
        setEditingInteraction: async (interaction: LeadInteraction | null) => {
            set({editingInteraction: interaction})
        },
        handleCreateInteraction: async (lead: Lead) => {
            try {
                console.log(lead);
                if (!lead.id) {
                    throw new Error('Lead ID is required for adding interactions');
                }
                const interactionData = lead.interactions[lead.interactions.length - 1];
                const response = await leadApi.addInteraction(lead.id, interactionData);

                // Update the selectedLead and leads array with the new interaction
                const {selectedLead, leads} = get();
                if (selectedLead && selectedLead.id === lead.id) {
                    set({selectedLead: lead});
                }

                // Also update the lead in the leads array
                const updatedLeads = leads.map(l => l.id === lead.id ? lead : l);
                set({leads: updatedLeads});

                console.log('-----------------------------');
                return response;
            } catch (error) {
                console.error("Error adding interaction:", error);
                console.log('111111111111111111111111111');
                throw error;
            }
        },
        handleDeleteInteraction: async (interactionId: string) => {
            const {selectedLead, leads} = get();
            if (!selectedLead) {
                console.error("No selected lead to delete interaction from");
                return;
            }
            try {
                if (!selectedLead.id) {
                    throw new Error('Lead ID is required for deleting interactions');
                }
                await leadApi.deleteInteraction(selectedLead.id, interactionId);
                // מעדכן את selectedLead בסטייט כך שיכיל את רשימת האינטראקציות לאחר מחיקה
                const updatedInteractions = selectedLead.interactions.filter(
                    (interaction) => interaction.id !== interactionId
                );
                const updatedSelectedLead = {
                    ...selectedLead,
                    interactions: updatedInteractions
                };

                // Update both selectedLead and the lead in the leads array
                const updatedLeads = leads.map(l =>
                    l.id === selectedLead.id ? updatedSelectedLead : l
                );

                set({
                    selectedLead: updatedSelectedLead,
                    leads: updatedLeads
                });
                console.log("✅ Interaction deleted successfully");
            } catch (error: any) {
                console.error("Error deleting interaction:", error);
            }
        }
        ,
        //--- תוספות להעלאת קובץ אקסל ---
        setUploadFile: (file: File | null) => {
            set({
                uploadFile: file,
                uploadStatus: "idle",
                uploadMessage: "",
            });
        },
        uploadExcelFile: async () => {
            const file = get().uploadFile;
            if (!file) {
                set({uploadStatus: "error", uploadMessage: "אין קובץ להעלאה"});
                return;
            }
            set({uploadStatus: "uploading", uploadMessage: "מעלה את הקובץ..."});
            try {
                const data = await leadApi.uploadExcel(file);
                set({
                    uploadStatus: "success",
                    uploadMessage: data.message || "הקובץ הועלה בהצלחה",
                });
                // ריענון רשימת הלידים לאחר ההעלאה
                await get().fetchLeads();
            } catch (error: any) {
                set({
                    uploadStatus: "error",
                    uploadMessage: error.message || "שגיאה לא צפויה בהעלאת הקובץ",
                });
            }
        },
    }
));